import { useFormik } from "formik";
import { motion } from "framer-motion";
import { useContext, useState } from "react";
import { FaEye, FaEyeSlash, FaShieldAlt } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import logo from "../assest/logo.png";
import SummaryApi from "../common";
import {
  APP_SHORT_NAME,
  EMAIL_PLACEHOLDER,
  LOGIN_SUBTITLE,
  LOGIN_TITLE,
  PASSWORD_PLACEHOLDER,
  ROUTES,
} from "../constants";
import Context from "../context";
import { request } from "../helpers/request";
import { loginSchema } from "../helpers/validationSchema";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [captchaData, setCaptchaData] = useState({ question: "", hash: "" });
  const [captchaInput, setCaptchaInput] = useState("");
  const [adminCode, setAdminCode] = useState("");
  const [generalError, setGeneralError] = useState("");
  const navigate = useNavigate();
  const { fetchUserDetails, fetchUserAddToCart } = useContext(Context);

  const fetchCaptcha = async () => {
    const res = await request(SummaryApi.getCaptcha);
    const data = await res.json();
    if (data.success)
      setCaptchaData({ question: data.question, hash: data.captchaHash });
  };

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      setGeneralError("");
      try {
        const dataResponse = await request(SummaryApi.signIn, {
          body: JSON.stringify({
            ...values,
            captchaSolution: captchaInput,
            captchaHash: captchaData.hash,
            adminCode,
          }),
        });
        const dataApi = await dataResponse.json();
        if (dataApi.success) {
          toast.success(dataApi.message);
          navigate(ROUTES.HOME);
          fetchUserDetails();
          fetchUserAddToCart();
        } else {
          if (dataApi.requires2FA) {
            setRequires2FA(true);
            toast.info(dataApi.message);
          } else {
            setGeneralError(dataApi.message);
            if (dataApi.showCaptcha) {
              setShowCaptcha(true);
              fetchCaptcha();
              setCaptchaInput("");
            }
          }
        }
      } catch {
        setGeneralError("An error occurred. Please try again.");
      }
    },
  });

  return (
    <section className="min-h-[calc(100vh-4rem)] flex">
      {/* Left panel — decorative (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-2/5 bg-primary flex-col justify-between p-12 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-16 left-16 w-64 h-64 rounded-full bg-white" />
          <div className="absolute bottom-16 right-8 w-48 h-48 rounded-full bg-accent" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full border-2 border-white" />
        </div>

        {/* Logo */}
        <Link
          to={ROUTES.HOME}
          className="flex items-center gap-3 relative z-10"
        >
          <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
            <img
              src={logo}
              alt={APP_SHORT_NAME}
              className="w-7 h-7 brightness-0 invert object-contain"
            />
          </div>
          <span className="font-black text-2xl text-white tracking-tight">
            {APP_SHORT_NAME}
          </span>
        </Link>

        {/* Content */}
        <div className="relative z-10 space-y-6">
          <div className="w-12 h-12 bg-accent/20 rounded-2xl flex items-center justify-center">
            <FaShieldAlt className="text-accent text-xl" />
          </div>
          <h2 className="text-3xl font-black text-white leading-tight">
            Your gear,
            <br />
            your safety.
          </h2>
          <p className="text-neutral-400 text-sm leading-relaxed">
            Sign in to access your orders, wishlist, and enjoy a seamless
            shopping experience.
          </p>
          <div className="flex gap-3">
            {[
              { value: "500+", label: "Products" },
              { value: "10k+", label: "Riders" },
              { value: "4.9", label: "Rating" },
            ].map(({ value, label }) => (
              <div
                key={label}
                className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 flex-1 text-center"
              >
                <div className="text-white font-black text-lg">{value}</div>
                <div className="text-neutral-500 text-xs">{label}</div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-neutral-600 text-xs relative z-10">
          © {new Date().getFullYear()} {APP_SHORT_NAME}. All rights reserved.
        </p>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center px-5 py-12 bg-surface-muted">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <Link
            to={ROUTES.HOME}
            className="flex items-center gap-2 mb-8 lg:hidden"
          >
            <img
              src={logo}
              alt={APP_SHORT_NAME}
              className="w-8 h-8 object-contain"
            />
            <span className="font-black text-xl text-primary">
              {APP_SHORT_NAME}
            </span>
          </Link>

          {/* Heading */}
          <div className="mb-8">
            <h1 className="text-3xl font-black text-primary tracking-tight mb-1">
              {LOGIN_TITLE}
            </h1>
            <p className="text-neutral-500 text-sm">{LOGIN_SUBTITLE}</p>
          </div>

          {/* Form */}
          <form onSubmit={formik.handleSubmit} className="space-y-4" noValidate>
            {/* Email */}
            <div>
              <label className="block text-2xs font-black uppercase tracking-widest text-neutral-400 mb-1.5 ml-0.5">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                placeholder={EMAIL_PLACEHOLDER}
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`input-base ${formik.touched.email && formik.errors.email ? "border-red-400 bg-red-50" : ""}`}
              />
              {formik.touched.email && formik.errors.email && (
                <p className="text-red-500 text-2xs font-semibold mt-1 ml-0.5">
                  {formik.errors.email}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5 ml-0.5 mr-0.5">
                <label className="text-2xs font-black uppercase tracking-widest text-neutral-400">
                  Password
                </label>
                <Link
                  to={ROUTES.FORGOT_PASSWORD}
                  className="text-2xs font-bold text-accent hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div
                className={`relative input-base flex items-center h-12 pr-0 ${formik.touched.password && formik.errors.password ? "border-red-400 bg-red-50" : ""}`}
              >
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder={PASSWORD_PLACEHOLDER}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="flex-1 h-full outline-none bg-transparent text-sm font-medium text-primary placeholder:text-neutral-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="px-3 h-full text-neutral-400 hover:text-primary transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {formik.touched.password && formik.errors.password && (
                <p className="text-red-500 text-2xs font-semibold mt-1 ml-0.5">
                  {formik.errors.password}
                </p>
              )}
            </div>

            {/* CAPTCHA */}
            {showCaptcha && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
              >
                <label className="block text-2xs font-black uppercase tracking-widest text-accent mb-1.5 ml-0.5">
                  Security Check: {captchaData.question}
                </label>
                <input
                  type="text"
                  placeholder="Enter answer"
                  value={captchaInput}
                  onChange={(e) => setCaptchaInput(e.target.value)}
                  className="input-base border-accent/30 bg-accent/5"
                  required
                />
              </motion.div>
            )}

            {/* 2FA */}
            {requires2FA && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
              >
                <label className="block text-2xs font-black uppercase tracking-widest text-accent mb-1.5 ml-0.5">
                  Admin 2FA Code
                </label>
                <input
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={adminCode}
                  onChange={(e) => setAdminCode(e.target.value)}
                  className="input-base border-accent/30 bg-accent/5"
                  required
                />
              </motion.div>
            )}

            {/* Error */}
            {generalError && (
              <div className="p-3 bg-red-50 border border-red-100 rounded-xl">
                <p className="text-red-600 text-xs font-semibold">
                  {generalError}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={formik.isSubmitting}
              className="w-full h-12 bg-primary text-white font-bold text-sm rounded-xl hover:bg-accent transition-all duration-300 shadow-button-primary active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            >
              {formik.isSubmitting ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <p className="mt-6 text-center text-neutral-500 text-sm">
            New to {APP_SHORT_NAME}?{" "}
            <Link
              to={ROUTES.SIGNUP}
              className="text-accent font-bold hover:underline"
            >
              Create account
            </Link>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Login;
